import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const BENEFITS = [
  { id: 1,  emoji: "🎯", title: "1-on-1 Mentorship",              desc: "Personal guidance built around your goals — not generic advice.",          accent: "#00FF88" },
  { id: 2,  emoji: "📈", title: "Live Market Training",           desc: "Real charts, real market hours. Mentors break down setups in real time.",   accent: "#00F0FF" },
  { id: 3,  emoji: "♾️", title: "Lifetime Mentorship",            desc: "Mentors stay with you long after the course — forever.",                   accent: "#00FF88" },
  { id: 4,  emoji: "💡", title: "Lifetime Doubt Sessions",        desc: "Live Q&A anytime. Zero backlog. Zero unanswered questions.",               accent: "#00F0FF" },
  { id: 5,  emoji: "🎬", title: "Lifetime Class Recordings",      desc: "Every session stored in HD. Rewatch anything, at your pace.",              accent: "#00FF88" },
  { id: 6,  emoji: "🖥️", title: "Trading Platform",               desc: "Hands-on with Dhan, TradingView & professional charting tools.",           accent: "#00F0FF" },
  { id: 7,  emoji: "🎓", title: "Completion Certificate",         desc: "Official JamaDhan credential — verified proof of your commitment.",        accent: "#00FF88" },
  { id: 8,  emoji: "💬", title: "Telegram Community",             desc: "Daily alerts, live trade calls, and market pulse from active traders.",    accent: "#00F0FF" },
  { id: 9,  emoji: "🔒", title: "Private Community",              desc: "Exclusive members-only inner circle. Peer reviews and accountability.",    accent: "#00FF88" },
  { id: 10, emoji: "▶️", title: "YouTube Video Access",           desc: "A curated library of concept breakdowns and strategy deep-dives.",        accent: "#00F0FF" },
  { id: 11, emoji: "📊", title: "Student LMS Dashboard",          desc: "One unified portal for classes, recordings, and all your materials.",     accent: "#00FF88" },
  { id: 12, emoji: "⚡", title: "Free JamaDhan Tools",            desc: "Risk Calculator, Position Sizer, AI tools — all included. ₹0 extra.",    accent: "#00F0FF" },
  { id: 13, emoji: "📓", title: "Trading Journal",                desc: "Log trades, review patterns, eliminate mistakes. Build discipline.",       accent: "#00FF88" },
  { id: 14, emoji: "🔄", title: "Batch Switch Option",            desc: "Missed your batch? Switch to another — no penalties, no questions.",      accent: "#00F0FF" },
  { id: 15, emoji: "🛡️", title: "Risk-First Teaching",            desc: "Capital protection, position sizing, and stop-loss in every lesson.",     accent: "#00FF88" },
];

function BenefitTile({ benefit, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-2xl p-6 border border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/[0.14] transition-all duration-300 cursor-default overflow-hidden"
      style={{ '--accent': benefit.accent }}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 20% 20%, ${benefit.accent}12 0%, transparent 65%)` }}
      />

      {/* Top accent line */}
      <div
        className="absolute top-0 left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `linear-gradient(to right, transparent, ${benefit.accent}80, transparent)` }}
      />

      {/* Number */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-2xl leading-none select-none">{benefit.emoji}</span>
        <span
          className="font-jetbrains text-[10px] font-bold tracking-[0.2em] opacity-40 group-hover:opacity-80 transition-opacity duration-300"
          style={{ color: benefit.accent }}
        >
          {String(benefit.id).padStart(2, '0')}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-white font-outfit font-bold text-[15px] sm:text-base leading-snug mb-2 group-hover:text-white transition-colors">
        {benefit.title}
      </h3>

      {/* Description */}
      <p className="text-[#64748B] font-plex text-[13px] leading-relaxed group-hover:text-[#94A3B8] transition-colors duration-300">
        {benefit.desc}
      </p>
    </motion.div>
  );
}

export default function StudentOfferingsCarousel() {
  const headingRef = useRef(null);
  const isHeadingInView = useInView(headingRef, { once: true, margin: "-60px" });

  return (
    <section
      id="student-offerings"
      className="relative bg-[#040608] py-24 sm:py-32 px-6 sm:px-10 lg:px-16 overflow-hidden"
      data-testid="student-offerings-section"
    >
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-40"
          style={{ background: 'radial-gradient(ellipse, rgba(0,255,136,0.06) 0%, transparent 70%)' }}
        />
      </div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.018] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">

        {/* ── HEADING ── */}
        <div ref={headingRef} className="text-center mb-16 sm:mb-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={isHeadingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="font-jetbrains text-[11px] tracking-[0.35em] uppercase text-[#00FF88] mb-4"
          >
            What you unlock as a student
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            animate={isHeadingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-outfit tracking-tight leading-[1.08] mb-5"
          >
            15 benefits.{' '}
            <span className="bg-gradient-to-r from-[#00FF88] to-[#00F0FF] bg-clip-text text-transparent">
              Zero compromise.
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={isHeadingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#64748B] text-base sm:text-lg font-plex max-w-xl mx-auto leading-relaxed"
          >
            Everything you need to go from complete beginner to consistently profitable trader.
          </motion.p>

          {/* Stats strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isHeadingInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="flex items-center justify-center gap-8 sm:gap-14 mt-10"
          >
            {[
              { val: "15+", label: "Benefits" },
              { val: "∞", label: "Lifetime access" },
              { val: "₹0", label: "Hidden charges" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-black font-outfit bg-gradient-to-r from-[#00FF88] to-[#00F0FF] bg-clip-text text-transparent leading-none">
                  {s.val}
                </div>
                <div className="text-[#64748B] text-xs font-plex mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ── BENEFIT TILES GRID ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {BENEFITS.map((benefit, i) => (
            <BenefitTile key={benefit.id} benefit={benefit} index={i} />
          ))}

          {/* Final CTA tile */}
          <motion.a
            href="/login"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="group relative rounded-2xl p-6 flex flex-col justify-between overflow-hidden cursor-pointer sm:col-span-2 lg:col-span-1"
            style={{
              background: 'linear-gradient(135deg, rgba(0,255,136,0.15) 0%, rgba(0,240,255,0.1) 100%)',
              border: '1px solid rgba(0,255,136,0.3)',
            }}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: 'linear-gradient(135deg, rgba(0,255,136,0.25) 0%, rgba(0,240,255,0.18) 100%)' }}
            />
            <div className="relative z-10">
              <span className="text-2xl mb-4 block">🚀</span>
              <h3 className="text-white font-outfit font-black text-xl leading-snug mb-2">
                Ready to start?
              </h3>
              <p className="text-[#94A3B8] font-plex text-[13px] leading-relaxed">
                Join the next free workshop and unlock all 15 benefits.
              </p>
            </div>
            <div className="relative z-10 mt-6 flex items-center gap-2 text-[#00FF88] font-outfit font-bold text-sm group-hover:gap-3 transition-all duration-300">
              <span>Join Free Workshop</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </motion.a>
        </div>

      </div>
    </section>
  );
}
